import axios from "axios";
import { compress } from "../helpers/common.helper";
const apiUri = import.meta.env.GATEWAY_URI;

export default function useChat() {
  const createSession = async (users, support) => {
    const _body = compress(JSON.stringify({ users, support, closed: false }));

    const result = await axios.post(`${apiUri}/api/v1/chatSessions`, _body, {
      headers: {
        "Content-Type": "text/plain",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return result?.data?.data;
  };

  const bookSessionByStaff = async () => {
    const result = await axios.post(
      `${apiUri}/api/v1/chatSessions/book`,
      null,
      {
        headers: {
          "Content-Type": "text/plain",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return result?.data?.data;
  };

  const createChat = async (
    sessionId,
    type,
    {
      _id,
      msg,
      location,
      quotationId,
      invoiceId,
      draft,
      files,
      fileType,
      replied,
      work,
    }
  ) => {
    const requestBody = {
      session: sessionId,
      type,
    };

    if (_id) requestBody._id = _id;
    if (msg) requestBody.msg = msg;
    if (quotationId) requestBody.quotation = quotationId;
    if (invoiceId) requestBody.invoice = invoiceId;
    if (draft) requestBody.draft = draft;
    if (fileType) requestBody.fileType = fileType;
    if (replied) requestBody.replied = replied;
    if (location) requestBody.location = location;
    if (work) requestBody.work = work;

    const _body = compress(JSON.stringify(requestBody));

    let data = new FormData();
    data.append("body", _body);

    for (const file of files ?? []) {
      data.append("files", file, file.name);
    }

    const result = await axios.post(`${apiUri}/api/v1/chats`, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        maxBodyLength: Infinity,
      },
    });

    return result?.data.data;
  };

  const queryChats = async (filter, page, limit, sort, abortSignal) => {
    // controller.abort();

    const reqBody = {};

    if (filter) reqBody.filter = filter;
    if (page) reqBody.page = page;
    if (limit) reqBody.limit = limit;
    if (sort) reqBody.sort = sort;

    const _body = compress(JSON.stringify(reqBody));
    const _options = {
      headers: {
        "Content-Type": "text/plain",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    };

    if (abortSignal) {
      _options.signal = abortSignal;
    }

    const result = await axios.post(
      `${apiUri}/api/v1/chats/query`,
      _body,
      _options
    );

    return result.data;
  };

  const querySessions = async (filter, page, limit, project, sort) => {
    const reqBody = {};

    if (filter) reqBody.filter = filter;
    if (page) reqBody.page = page;
    if (limit) reqBody.limit = limit;
    if (project) reqBody.project = project;
    if (sort) reqBody.sort = sort;

    const _body = compress(JSON.stringify(reqBody));

    const result = await axios.post(
      `${apiUri}/api/v1/chatSessions/query`,
      _body,
      {
        headers: {
          "Content-Type": "text/plain",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    return result.data;
  };

  const getSession = async (id) => {
    const result = await axios.get(`${apiUri}/api/v1/chatSessions/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return result.data?.data;
  };

  const getChat = async (id) => {
    const result = await axios.get(`${apiUri}/api/v1/chats/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return result.data?.data;
  };

  const closeSession = async (id) => {
    const result = await axios.post(
      `${apiUri}/api/v1/chatSessions/${id}/close`,
      null,
      {
        headers: {
          "Content-Type": "text/plain",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return result.data;
  };

  const downloadSessionChats = async (id) => {
    const result = await axios.post(
      `${apiUri}/api/v1/chatSessions/${id}/chats/download`,
      null,
      {
        headers: {
          "Content-Type": "text/plain",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return result?.data?.data;
  };

  const markRead = async (chatIds) => {
    const _body = compress(JSON.stringify({ _ids: chatIds }));

    const result = await axios.post(`${apiUri}/api/v1/chats/read`, _body, {
      headers: {
        "Content-Type": "text/plain",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return result?.data?.data;
  };

  const getFiles = async (chatIds) => {
    const result = await axios.get(`${apiUri}/api/v1/chats/files`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      params: {
        ids: chatIds.join(","),
      },
    });

    return result?.data?.data;
  };

  const deleteChat = async (chatId) => {
    const result = await axios.delete(`${apiUri}/api/v1/chats/${chatId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    return result?.data.data;
  };

  const pinChat = async (chatId) => {
    const result = await axios.post(
      `${apiUri}/api/v1/chats/${chatId}/pin`,
      null,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    return result?.data;
  };

  const unpinChat = async (chatId) => {
    const result = await axios.post(
      `${apiUri}/api/v1/chats/${chatId}/unpin`,
      null,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    return result?.data;
  };

  const pinSession = async (sessionId) => {
    const result = await axios.post(
      `${apiUri}/api/v1/chatSessions/${sessionId}/pin`,
      null,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    return result?.data;
  };

  const unpinSession = async (sessionId) => {
    const result = await axios.post(
      `${apiUri}/api/v1/chatSessions/${sessionId}/unpin`,
      null,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    return result?.data;
  };

  const hideSession = async (userId, sessionId) => {
    const _body = compress(
      JSON.stringify({ session: sessionId, since: new Date() })
    );

    const result = await axios.post(
      `${apiUri}/api/v1/users/${userId}/chat/hidden`,
      _body,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "text/plain",
        },
      }
    );

    return result?.data;
  };

  const rateSession = async (sessionId, score, comment) => {
    const _body = compress(JSON.stringify({ rate: score, comment }));

    const result = await axios.post(
      `${apiUri}/api/v1/chatSessions/${sessionId}/rating`,
      _body,
      {
        headers: {
          "Content-Type": "text/plain",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return result?.data?.data;
  };

  const CHAT_TYPE = {
    DATE: 0,
    MESSAGE: 1,
    IMPRESSION: 2,
    TENDERING_NDA_SENT: 301,
    TENDERING_NDA_SIGNED: 302,
    TENDERING_SELECTED: 303,
    TENDERING_QUOTATION_SENT: 304,
    TENDERING_SCHEDULE_SENT: 305,
    TENDERING_WON: 306,
    TENDERING_SIGN_CONTRACT: 307,
    TENDERING_CONTRACT_SIGNED: 308,
    TENDERING_WORK_SUBMITTED: 309,
    TENDERING_WORK_RESUBMITTED: 310,
    TENDERING_WORK_APPROVED: 311,
    INVOICE: 4,
    SUBMISSION: 5,
  };

  return {
    createSession,
    querySessions,
    getSession,
    createChat,
    queryChats,
    bookSessionByStaff,
    closeSession,
    downloadSessionChats,
    CHAT_TYPE,
    markRead,
    getFiles,
    deleteChat,
    pinChat,
    unpinChat,
    getChat,
    pinSession,
    unpinSession,
    hideSession,
    rateSession,
  };
}
